import type {ActionEnvelope, ChatAction, ChatState, StateAction} from '@microsoft/agent-host-protocol'
import {chatReducer} from '@microsoft/agent-host-protocol'
import type {ObservableRelaySink} from './relay-registry'

const ACTION_TOOL_CALL_START = 'chat/toolCallStart'
const ACTION_TOOL_CALL_READY = 'chat/toolCallReady'
const ACTION_TOOL_CALL_CONFIRMED = 'chat/toolCallConfirmed'
const ACTION_TOOL_CALL_COMPLETE = 'chat/toolCallComplete'
const ACTION_TOOL_CALL_RESULT_CONFIRMED = 'chat/toolCallResultConfirmed'
const ACTION_SESSION_TITLE_CHANGED = 'session/titleChanged'
const PART_TOOL_CALL = 'toolCall'
const TOOL_STATUS_STREAMING = 'streaming'
const TOOL_STATUS_RUNNING = 'running'
const TOOL_STATUS_PENDING_CONFIRMATION = 'pending-confirmation'
const TOOL_STATUS_PENDING_RESULT_CONFIRMATION = 'pending-result-confirmation'

// Terminal tool-call statuses: a buffered Ready can never surface a confirmation
// once its tool reaches one of these, so we stop tracking it.
const TERMINAL_TOOL_STATUSES: ReadonlySet<string> = new Set(['completed', 'cancelled'])

// Turn-terminal actions. AHP's reducer force-cancels every non-terminal tool
// call in the active turn on these, so a buffered Ready for that turn can never
// surface a valid confirmation again. We drop those readies to (a) bound
// retention — a Ready whose `chat/toolCallStart` never arrived leaves no tool
// part behind, so it would otherwise linger for the store's lifetime — and (b)
// stop a later reconnect snapshot, which may report the tool as `streaming`
// again, from re-opening the already-cancelled prompt.
const TURN_TERMINAL_ACTIONS: ReadonlySet<string> = new Set(['chat/turnComplete', 'chat/turnCancelled', 'chat/error'])

/**
 * Narrow a wire `StateAction` to the chat-scoped subset the reducer accepts.
 */
function isChatAction(action: StateAction): action is ChatAction {
  return action.type.startsWith('chat/')
}

function toolCallKey(action: StateAction): string | undefined {
  const toolCallId = (action as {toolCallId?: unknown}).toolCallId
  return typeof toolCallId === 'string' ? toolCallId : undefined
}

function turnKey(action: StateAction): string | undefined {
  const turnId = (action as {turnId?: unknown}).turnId
  return typeof turnId === 'string' ? turnId : undefined
}

interface ToolReplayState {
  readyAction: ChatAction
  confirmedAction?: ChatAction
  completeAction?: ChatAction
}

/**
 * Current status of the tool call with `toolCallId`, or `undefined` when no such
 * tool part exists yet (its `chat/toolCallStart` has not been folded in).
 * `toolCallId` is session-unique by the copilot-host producer contract, so this
 * intentionally searches across turns without including `turnId` in the key.
 */
function toolCallStatus(state: ChatState, toolCallId: string): string | undefined {
  const turns = [...(state.turns ?? []), ...(state.activeTurn ? [state.activeTurn] : [])]
  for (const turn of turns) {
    for (const part of turn.responseParts) {
      if (part.kind === PART_TOOL_CALL && part.toolCall.toolCallId === toolCallId) {
        return part.toolCall.status
      }
    }
  }
  return undefined
}

/**
 * Maintains the AHP `ChatState` for one relay session and exposes it
 * as an external store for `useSyncExternalStore`.
 */
export class AhpChatStore implements ObservableRelaySink {
  #state: ChatState | undefined
  #seeded = false
  #disposed = false
  // Work around copilotd occasionally publishing permission prompts
  // before their matching tool start. The observed wire order is:
  // `chat/toolCallReady` (confirmed absent, carries confirmation UI data) then
  // `chat/toolCallStart` for the same toolCallId. AHP's official chatReducer
  // intentionally ignores Ready unless the tool already exists in streaming or
  // running state, so without this buffer the UI renders a bare streaming tool
  // (often as "undefined") and never shows confirmation choices.
  //
  // Upstream copilotd places these on separate async paths:
  // - SDK `tool.execution_start` -> SessionPipelineEvent::Cli -> ChatToolCallStart
  // - permission callback -> SessionPipelineEvent::Permission -> ChatToolCallReady
  // The host should serialize these so Start reaches clients before Ready; once
  // that is fixed, this compatibility buffer can be removed.
  //
  // The reorder became far more common under copilot-host protocol 0.5.1, which
  // routinely emits Ready before Start. A reconnect (which happens at least once
  // per session) re-seeds state via `applySnapshot`, and the host's snapshot
  // reports the tool as `streaming` again because its confirmation lives on a
  // separate path. We therefore RETAIN a buffered Ready across snapshots and
  // re-derive the confirmation whenever its tool is present and still
  // `streaming` (see `#drainPendingToolReady`).
  //
  // Retain the non-terminal transition chain (Ready, Confirmed, and a Complete
  // that requires result confirmation) so a reconnect snapshot that resets the
  // tool to `streaming` can reconstruct the latest prompt without stalling.
  // Retention is bounded by result confirmation, a terminal snapshot status, or
  // the turn ending.
  readonly #pendingToolReplay = new Map<string, ToolReplayState>()
  readonly #listeners = new Set<() => void>()

  /**
   * No-op: the store owns its own state, so there is nothing to take ownership
   * of before events arrive. Present to satisfy the {@link RelaySessionSink}
   * contract the relay registry drives.
   */
  start(): void {}

  /** Seed (or re-seed, on reconnect) the state wholesale from a snapshot. */
  applySnapshot(state: ChatState): void {
    if (this.#disposed) return
    this.#state = state
    this.#seeded = true
    // Re-derive buffered readies across reconnect snapshots (0.5.1 emits Ready
    // before Start, and a reconnect snapshot resets a locally-confirmed tool to
    // `streaming`): surface any whose tool is present, leave the rest buffered
    // for a future Start rather than dropping the confirmation.
    this.#drainPendingToolReady()
    this.#emit()
  }

  /** Fold a single live action into the current state. */
  applyAction(env: ActionEnvelope): void {
    if (this.#disposed || !this.#seeded || !this.#state) return

    if (env.action.type === ACTION_SESSION_TITLE_CHANGED) {
      const nextTitle = (env.action as {title?: unknown}).title
      if (typeof nextTitle === 'string' && nextTitle !== this.#state.title) {
        this.#state = {...this.#state, title: nextTitle}
        this.#emit()
      }
      return
    }

    if (!isChatAction(env.action)) return

    if (env.action.type === ACTION_TOOL_CALL_READY) {
      const toolCallId = toolCallKey(env.action)
      if (toolCallId && toolCallStatus(this.#state, toolCallId) === undefined) {
        this.#pendingToolReplay.set(toolCallId, {readyAction: env.action})
        return
      }
    }

    this.#state = chatReducer(this.#state, env.action)

    if (env.action.type === ACTION_TOOL_CALL_READY) {
      const toolCallId = toolCallKey(env.action)
      const status = toolCallId ? toolCallStatus(this.#state, toolCallId) : undefined
      if (toolCallId && (status === TOOL_STATUS_PENDING_CONFIRMATION || status === TOOL_STATUS_RUNNING)) {
        this.#pendingToolReplay.set(toolCallId, {readyAction: env.action})
      }
    } else if (env.action.type === ACTION_TOOL_CALL_START) {
      // `chat/toolCallStart` is the only action that introduces the `streaming`
      // tool a buffered Ready targets, so gate the (history-scanning) drain to
      // it — streamed `chat/delta`/`chat/toolCallDelta` tokens must not pay that
      // cost on every token.
      this.#drainPendingToolReady()
    } else if (env.action.type === ACTION_TOOL_CALL_CONFIRMED) {
      const toolCallId = toolCallKey(env.action)
      const replay = toolCallId ? this.#pendingToolReplay.get(toolCallId) : undefined
      if (replay) replay.confirmedAction = env.action
    } else if (env.action.type === ACTION_TOOL_CALL_COMPLETE) {
      const toolCallId = toolCallKey(env.action)
      const replay = toolCallId ? this.#pendingToolReplay.get(toolCallId) : undefined
      const status = toolCallId ? toolCallStatus(this.#state, toolCallId) : undefined
      if (replay && (status === undefined || status === TOOL_STATUS_PENDING_RESULT_CONFIRMATION)) {
        replay.completeAction = env.action
      } else if (toolCallId) {
        this.#pendingToolReplay.delete(toolCallId)
      }
    } else if (env.action.type === ACTION_TOOL_CALL_RESULT_CONFIRMED) {
      const toolCallId = toolCallKey(env.action)
      if (toolCallId) this.#pendingToolReplay.delete(toolCallId)
    } else if (TURN_TERMINAL_ACTIONS.has(env.action.type)) {
      // The turn ended: the reducer force-cancelled its tools, so forget every
      // buffered Ready for that turn. Bounds retention when Start never arrived
      // and prevents a reconnect snapshot from re-opening a cancelled prompt.
      const turnId = turnKey(env.action)
      if (turnId) this.#forgetTurnReadies(turnId)
    }

    this.#emit()
  }

  /**
   * Re-derive buffered out-of-order tool transitions after a snapshot or Start.
   * Historical turns and terminal tools retire their replay state. Streaming
   * tools replay Ready, Confirmed, and Complete when present, so reconnect restores
   * either the execution state or the pending result-confirmation prompt.
   */
  #drainPendingToolReady(): void {
    if (!this.#state || this.#pendingToolReplay.size === 0) return
    for (const [toolCallId, replay] of this.#pendingToolReplay) {
      const turnId = turnKey(replay.readyAction)
      if (turnId && this.#state.turns.some(turn => turn.id === turnId)) {
        this.#pendingToolReplay.delete(toolCallId)
        continue
      }

      const status = toolCallStatus(this.#state, toolCallId)
      if (status === undefined) continue // Start not folded in yet — keep buffered.
      if (status === TOOL_STATUS_STREAMING) {
        this.#state = chatReducer(this.#state, replay.readyAction)
        if (replay.confirmedAction) {
          this.#state = chatReducer(this.#state, replay.confirmedAction)
        }
        if (replay.completeAction) {
          this.#state = chatReducer(this.#state, replay.completeAction)
          const replayedStatus = toolCallStatus(this.#state, toolCallId)
          if (replayedStatus && TERMINAL_TOOL_STATUSES.has(replayedStatus)) {
            this.#pendingToolReplay.delete(toolCallId)
          }
        }
      } else if (TERMINAL_TOOL_STATUSES.has(status)) {
        // Confirmation can never apply to a finished tool; stop tracking it.
        this.#pendingToolReplay.delete(toolCallId)
      }
    }
  }

  /**
   * Forget every buffered `chat/toolCallReady` belonging to `turnId`. Called on
   * turn-terminal actions (`chat/turnComplete` / `turnCancelled` / `error`),
   * which the reducer treats by force-cancelling the turn's non-terminal tools.
   * A Ready for a finished turn can never surface a valid confirmation again, so
   * dropping it bounds retention (the Start may never have arrived, leaving no
   * tool part to terminate it via `#drainPendingToolReady`) and prevents a later
   * reconnect snapshot from re-opening the cancelled prompt.
   */
  #forgetTurnReadies(turnId: string): void {
    for (const [toolCallId, replay] of this.#pendingToolReplay) {
      if (turnKey(replay.readyAction) === turnId) this.#pendingToolReplay.delete(toolCallId)
    }
  }

  dispose(): void {
    this.#disposed = true
    this.#pendingToolReplay.clear()
    this.#listeners.clear()
  }

  /** Current `ChatState`, or `undefined` before the first snapshot. */
  getSnapshot(): ChatState | undefined {
    return this.#state
  }

  /**
   * Register a listener fired on every state change. Returns an unsubscribe function.
   */
  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener)
    return () => this.#listeners.delete(listener)
  }

  #emit(): void {
    for (const listener of this.#listeners) listener()
  }
}
